import { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/AuthContext";
import {
  ChartBar, Users, UserPlus, ListBullets, EnvelopeSimple, Gear,
  ArrowLeft, CircleNotch, List, X, ShieldWarning,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/admin", icon: ChartBar, label: "Dashboard", exact: true },
  { to: "/admin/users", icon: Users, label: "Users" },
  { to: "/admin/signups", icon: UserPlus, label: "New Signups" },
  { to: "/admin/activity", icon: ListBullets, label: "Activity Log" },
  { to: "/admin/email", icon: EnvelopeSimple, label: "Email Users" },
  { to: "/admin/settings", icon: Gear, label: "Settings" },
];

export default function AdminLayout() {
  const { user, isAuthenticated, loading, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      login({ appState: { returnTo: "/admin" } });
    }
  }, [loading, isAuthenticated, login]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <CircleNotch className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <ShieldWarning className="w-14 h-14 text-red-400 mx-auto mb-4" weight="fill" />
          <h1 className="font-display font-bold text-2xl text-slate-900 mb-2">403 Forbidden</h1>
          <p className="text-sm text-slate-500 mb-6">You don't have permission to access this page.</p>
          <Link to="/" className="text-sm text-teal font-medium hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const isActive = (path, exact) => exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen flex bg-slate-100">
      {/* Sidebar — Desktop */}
      <aside className="hidden lg:flex lg:flex-col w-60 bg-slate-900 text-white fixed inset-y-0 left-0 z-40">
        <div className="px-5 py-5 border-b border-white/10">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Admin Panel</p>
          <p className="text-sm font-medium text-white truncate mt-1">{user.name || user.email}</p>
          <p className="text-xs text-slate-400 truncate">{user.email}</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map(({ to, icon: Icon, label, exact }) => (
            <Link
              key={to}
              to={to}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(to, exact)
                  ? "bg-teal text-white"
                  : "text-slate-300 hover:bg-white/10 hover:text-white"
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" weight={isActive(to, exact) ? "fill" : "regular"} />
              {label}
            </Link>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
            <ArrowLeft className="w-5 h-5" /> Back to Site
          </Link>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 bg-slate-900 text-white z-40 flex items-center justify-between h-14 px-4">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Admin</p>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 text-slate-300 hover:text-white">
          {sidebarOpen ? <X className="w-5 h-5" /> : <List className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50" onClick={() => setSidebarOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <aside className="absolute left-0 inset-y-0 w-64 bg-slate-900 text-white p-4 space-y-1" onClick={e => e.stopPropagation()}>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Admin Panel</p>
            {navItems.map(({ to, icon: Icon, label, exact }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(to, exact) ? "bg-teal text-white" : "text-slate-300 hover:bg-white/10"
                }`}
              >
                <Icon className="w-5 h-5" /> {label}
              </Link>
            ))}
            <hr className="border-white/10 my-3" />
            <Link to="/" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" /> Back to Site
            </Link>
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-60 min-h-screen pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
